import React, { useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { LinkedIn, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';

const PageContainer = styled.div`
  background-color: var(--color-bg);
  min-height: 100vh;
  padding-bottom: 120px;
`;

const Hero = styled.div`
  padding: 120px 24px 80px;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-family: var(--font-serif);
  font-size: clamp(3rem, 6vw, 5rem);
  color: var(--color-text);
  margin-bottom: 24px;
  line-height: 1.1;
`;

const Subtitle = styled.p`
  font-family: var(--font-sans);
  font-size: 1.25rem;
  color: var(--color-text-light);
  line-height: 1.6;
`;

const Section = styled.section`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  margin-bottom: 120px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  border-bottom: 2px solid var(--color-text);
  padding-bottom: 24px;
  margin-bottom: 60px;
  flex-wrap: wrap;
  gap: 20px;
`;

const YearTitle = styled.h2`
  font-family: var(--font-serif);
  font-size: 3rem;
  color: var(--color-text);
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 40px 24px;
`;

const MemberCard = styled(motion.div)`
  position: relative;
  group;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 3/4;
  overflow: hidden;
  margin-bottom: 24px;
  background: #f0f0f0;
  border-radius: 4px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
    filter: grayscale(20%);
  }

  ${MemberCard}:hover img {
    transform: scale(1.05);
    filter: grayscale(0%);
  }
`;

const Meta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const Info = styled.div`
  flex: 1;
`;

const Role = styled.div`
  font-family: var(--font-sans);
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-primary-blue);
  margin-bottom: 8px;
  font-weight: 600;
`;

const Name = styled.h3`
  font-family: var(--font-serif);
  font-size: 1.75rem;
  color: var(--color-text);
  margin: 0;
  font-weight: 400;
`;

const SocialLink = styled.a`
  color: var(--color-text-light);
  transition: color 0.2s;
  padding: 4px;

  &:hover {
    color: #0077b5;
  }
`;

const ArchiveButton = styled.button`
  background: none;
  border: none;
  border-bottom: 1px solid var(--color-text-light);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 12px 0;
  width: 100%;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s;

  &:hover {
    color: var(--color-primary-blue);
    border-color: var(--color-primary-blue);
  }
`;

const ArchiveGrid = styled(motion.div)`
  margin-top: 40px;
  overflow: hidden;
`;

const picture_prefix = `${process.env.PUBLIC_URL}/photoDuComite/`;

const committees = [
    {
        year: "2025–2026",
        groupPhotos: [],
        members: [
            { name: "Solal Ben Hamou", role: "President", image: "solal1.jpg", linkedin: "https://www.linkedin.com/in/solal-benhamou-8394b2242/" },
            { name: "Sacha Bilbaum", role: "Vice-President", image: "sasha.jpg", linkedin: "https://www.linkedin.com/in/sasha-blibaum-534a40373/" },
            { name: "Ruben Lellouche", role: "Treasurer", image: "ruben.jpg", linkedin: "https://www.linkedin.com/in/ruben-lellouche-578618317/" },
            { name: "Valentine Wien", role: "Events Manager", image: "valentine.jpg", linkedin: "https://www.linkedin.com/in/valentine-wien-5a7858318/" },
            { name: "Eliot Pailloucq", role: "Outreach Manager", image: "eliot.jpg", linkedin: "https://www.linkedin.com/in/eliot-pailloucq-906a0a1ba/" },
            { name: "Valentin Walendy", role: "IT Manager", image: "valentin.jpg", linkedin: "https://www.linkedin.com/in/valentin-walendy-a16ba2328/" },
            { name: "Juliette Meyer", role: "Communication Manager", image: "juliette.jpg", linkedin: "https://www.linkedin.com/in/juliette-meyer-b813b2388/" },
            { name: "Jérémie Kemoun", role: "Communication Manager", image: "jeremie.jpg", linkedin: "https://www.linkedin.com/in/jérémie-k-baa528291/" },
        ],
    },
    {
        year: "2024–2025",
        groupPhotos: ["committee24251.png", "committee24252.png"],
        members: [
            { name: "Yshai Dinée-Baumgarten", role: "President", image: "yshai.png", linkedin: "https://www.linkedin.com/in/yshai-db/" },
            { name: "Raphaël Benichou", role: "Vice-President", image: "raph.png", linkedin: "https://www.linkedin.com/in/rapha%C3%ABl-benichou/" },
            { name: "Solal Ben Hamou", role: "Treasurer", image: "solal.png", linkedin: "https://www.linkedin.com/in/solal-benhamou-8394b2242/" },
            { name: "Andy Bittan", role: "Events Manager", image: "andy.png", linkedin: "https://www.linkedin.com/in/andy-bittan/" },
            { name: "Elior Papiernik", role: "General Secretary", image: "elior.png", linkedin: "https://www.linkedin.com/in/elior-papiernik-854240174" },
            { name: "Ethan Boren", role: "Logistic Manager", image: "ethan.png", linkedin: "https://www.linkedin.com/in/ethan-boren", objectPosition: "center 15%" },
            { name: "Gabriel Taieb", role: "IT Manager", image: "gabriel.png", linkedin: "https://www.linkedin.com/in/gabriel-taieb-812798227/" },
            { name: "Yael Soussan", role: "Communication Manager", image: "yael.png", linkedin: "https://www.linkedin.com/in/ya%C3%ABl-soussan-b66418306/" },
            { name: "Yanael Grundland", role: "Communication Manager", image: "yana.png", linkedin: "https://www.linkedin.com/in/yana%C3%ABl-grundland-99304630a/" },
        ],
    }
];

const ComiteeMembers = () => {
    const [expandedYear, setExpandedYear] = useState(null);

    const toggleYear = (year) => {
        setExpandedYear(expandedYear === year ? null : year);
    };

    return (
        <PageContainer>
            <Hero>
                <Title>The Team</Title>
                <Subtitle>
                    The dedicated individuals shaping the future of SHALOM at EPFL.
                </Subtitle>
            </Hero>

            {/* Current Committee - Always Visible & Prominent */}
            <Section>
                <SectionHeader>
                    <YearTitle>{committees[0].year}</YearTitle>
                    <span style={{ fontFamily: 'var(--font-sans)', color: 'var(--color-text-light)' }}>Current Board</span>
                </SectionHeader>

                <Grid>
                    {committees[0].members.map((member, idx) => (
                        <MemberCard
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1 }}
                        >
                            <ImageContainer>
                                <img
                                    src={`${picture_prefix}${member.image}`}
                                    alt={member.name}
                                    style={{ objectPosition: member.objectPosition || 'center' }}
                                />
                            </ImageContainer>
                            <Meta>
                                <Info>
                                    <Role>{member.role}</Role>
                                    <Name>{member.name}</Name>
                                </Info>
                                {member.linkedin && member.linkedin !== "#" && (
                                    <SocialLink href={member.linkedin} target="_blank" aria-label="LinkedIn">
                                        <LinkedIn fontSize="large" />
                                    </SocialLink>
                                )}
                            </Meta>
                        </MemberCard>
                    ))}
                </Grid>
            </Section>

            {/* Previous Committees - Archive Style */}
            <Section>
                <SectionHeader>
                    <YearTitle>Archive</YearTitle>
                </SectionHeader>

                {committees.slice(1).map((committee, idx) => (
                    <div key={idx} style={{ marginBottom: '40px' }}>
                        <ArchiveButton onClick={() => toggleYear(committee.year)}>
                            {committee.year} Committee
                            {expandedYear === committee.year ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </ArchiveButton>

                        <AnimatePresence>
                            {expandedYear === committee.year && (
                                <ArchiveGrid
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.4, ease: "easeInOut" }}
                                >
                                    <div style={{ paddingTop: '40px' }}>
                                        <Grid>
                                            {committee.members.map((member, mIdx) => (
                                                <MemberCard key={mIdx}>
                                                    <ImageContainer>
                                                        <img
                                                            src={`${picture_prefix}${member.image}`}
                                                            alt={member.name}
                                                            style={{ objectPosition: member.objectPosition || 'center' }}
                                                        />
                                                    </ImageContainer>
                                                    <Meta>
                                                        <Info>
                                                            <Role>{member.role}</Role>
                                                            <Name>{member.name}</Name>
                                                        </Info>
                                                        {member.linkedin && (
                                                            <SocialLink href={member.linkedin} target="_blank">
                                                                <LinkedIn />
                                                            </SocialLink>
                                                        )}
                                                    </Meta>
                                                </MemberCard>
                                            ))}
                                        </Grid>
                                    </div>
                                </ArchiveGrid>
                            )}
                        </AnimatePresence>
                    </div>
                ))}
            </Section>
        </PageContainer>
    );
};

export default ComiteeMembers;